import { computed, makeObservable, observable, action } from 'mobx'
import { StaticModelCollection } from '../../firestore-mobx/model'
import { SectionAssignmentPeerReviewQuestion } from '../../models/SectionAssignmentPeerReviewQuestion'
import { RoomStateUPR } from '../../models/RoomStateUPR'
import { RoomStateUPRNote } from '../../models/RoomStateUPRNote'
import { RoomStateUPRPeerReview } from '../../models/RoomStateUPRPeerReview'
import { getPeerReviewQuestionsStream } from '../../firestore/SectionAssignmentPeerReviewQuestion'
import { getPeerReviewNotesForAssignment } from '../../firestore/RoomStateUPRNote'
import { getUserPeerReviewsForAssignment } from '../../firestore/RoomStateUPR'
import { getPeerReviewsForAssignment } from '../../firestore/RoomStateUPRPeerReview'
import type { RoomState } from '../../models/RoomState'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import { Cubit } from '../core'
import { guardOnLoaded, type LoadingState } from '../../util/guardOn'

export type PeerReviewRevieweeSortStrategy = 'name' | 'average' | string

export type PeerReviewWaitingReviewerSortStrategy =
  | 'name'
  | 'scheduledAt'
  | 'completedAt'
  | 'daysOverdue'

export class InstructorPeerReviewController extends Cubit {
  repository: FirebaseRepository
  sectionId: string
  assignmentId: string
  @observable peerReviewExcludesSelfReview: boolean

  @observable
  peerReviewQuestions: StaticModelCollection<SectionAssignmentPeerReviewQuestion>

  roomStates: StaticModelCollection<RoomState>
  roomStateUPRs: StaticModelCollection<RoomStateUPR>
  roomStateUPRNotes: StaticModelCollection<RoomStateUPRNote>
  roomStateUPRReviewsByPeer: StaticModelCollection<RoomStateUPRPeerReview>
  roomStateUPRReviewsBySelf: StaticModelCollection<RoomStateUPRPeerReview>

  @observable
  revieweeSortStrategy: PeerReviewRevieweeSortStrategy = 'name'

  @observable
  revieweeSortOrder: 'asc' | 'desc' = 'asc'

  @observable
  waitingReviewerSortStrategy: PeerReviewWaitingReviewerSortStrategy =
    'daysOverdue'

  @observable waitingReviewerSortOrder: 'asc' | 'desc' = 'desc'

  private onQuestionsLoaded?: () => void

  constructor(
    repository: FirebaseRepository,
    {
      sectionId,
      assignmentId,
      peerReviewExcludesSelfReview,
      roomStates,
      onQuestionsLoaded,
    }: {
      sectionId: string
      assignmentId: string
      peerReviewExcludesSelfReview: boolean
      roomStates: StaticModelCollection<RoomState>
      onQuestionsLoaded?: () => void
    }
  ) {
    super()
    this.repository = repository
    this.sectionId = sectionId
    this.assignmentId = assignmentId
    this.peerReviewExcludesSelfReview = peerReviewExcludesSelfReview
    this.roomStates = roomStates
    this.onQuestionsLoaded = onQuestionsLoaded

    this.peerReviewQuestions =
      SectionAssignmentPeerReviewQuestion.emptyCollection(repository)
    this.roomStateUPRs = RoomStateUPR.emptyCollection(repository)
    this.roomStateUPRNotes = RoomStateUPRNote.emptyCollection(repository)
    this.roomStateUPRReviewsByPeer =
      RoomStateUPRPeerReview.emptyCollection(repository)
    this.roomStateUPRReviewsBySelf =
      RoomStateUPRPeerReview.emptyCollection(repository)

    makeObservable(this)
  }

  @action
  setRevieweeSortingStrategy(
    strategy: PeerReviewRevieweeSortStrategy,
    order: 'asc' | 'desc'
  ): void {
    this.revieweeSortStrategy = strategy
    this.revieweeSortOrder = order
  }

  @action
  setWaitingReviewerSortingStrategy(
    strategy: PeerReviewWaitingReviewerSortStrategy,
    order: 'asc' | 'desc'
  ): void {
    this.waitingReviewerSortStrategy = strategy
    this.waitingReviewerSortOrder = order
  }

  initialize(): void {
    this.addStream(
      getPeerReviewQuestionsStream(this.repository, {
        sectionId: this.sectionId,
        assignmentId: this.assignmentId,
      }),
      (questions) => {
        const sortedQuestions = [...questions].sort(
          (a, b) => a.data.prOrder - b.data.prOrder
        )
        this.peerReviewQuestions.replaceModels(sortedQuestions)
        if (this.onQuestionsLoaded) {
          this.onQuestionsLoaded()
        }
      }
    )

    this.addStream(
      getPeerReviewsForAssignment(this.repository, {
        sectionId: this.sectionId,
        assignmentId: this.assignmentId,
      }),
      (peerReviews) => {
        const reviewsByPeer: RoomStateUPRPeerReview[] = []
        const reviewsBySelf: RoomStateUPRPeerReview[] = []

        for (const peerReview of peerReviews) {
          if (peerReview.data.userId === peerReview.data.reviewerUserId) {
            reviewsBySelf.push(peerReview)
          } else {
            reviewsByPeer.push(peerReview)
          }
        }

        this.roomStateUPRReviewsByPeer.replaceModels(reviewsByPeer)
        this.roomStateUPRReviewsBySelf.replaceModels(reviewsBySelf)
      }
    )

    this.addStream(
      getPeerReviewNotesForAssignment(this.repository, {
        sectionId: this.sectionId,
        assignmentId: this.assignmentId,
      }),
      (notes) => {
        this.roomStateUPRNotes.replaceModels(notes)
      }
    )

    this.addStream(
      getUserPeerReviewsForAssignment(this.repository, {
        sectionId: this.sectionId,
        assignmentId: this.assignmentId,
      }),
      (userPeerReviews) => {
        this.roomStateUPRs.replaceModels(userPeerReviews)
      }
    )
  }

  @computed
  get peerReviewsEnabled(): LoadingState<boolean> {
    return guardOnLoaded(this.peerReviewQuestions, (questions) => {
      return questions.length > 0
    })
  }

  @computed
  get atLeastOneUserInPeerReviewForAssignment(): LoadingState<boolean> {
    return guardOnLoaded(this.roomStateUPRs, (userPeerReviewRecords) => {
      return userPeerReviewRecords.length > 0
    })
  }

  @computed
  get validUserPeerReviews(): LoadingState<RoomStateUPR[]> {
    return guardOnLoaded(
      [this.roomStateUPRs, this.roomStates],
      ([userPeerReviews, roomStates]) => {
        return userPeerReviews.filter((userPeerReview) => {
          const roomState = roomStates.find(
            (rs) => rs.id === userPeerReview.data.roomId
          )
          if (!roomState) return false

          return roomState.data.userIds.includes(userPeerReview.data.userId)
        })
      }
    )
  }

  @computed
  get peerReviewsByRoomReviewee(): LoadingState<
    Map<string, Map<string, PeerReviewsOfRoomUserData>>
  > {
    return guardOnLoaded(
      [
        this.validUserPeerReviews,
        this.roomStateUPRReviewsByPeer,
        this.roomStateUPRReviewsBySelf,
        this.peerReviewQuestions,
        this.roomStates,
        this.roomStateUPRNotes,
      ],
      ([
        validUserPeerReviews,
        peerReviews,
        selfReviews,
        questions,
        roomStates,
        notes,
      ]) => {
        // First, group by roomId, then by userId
        const byRoom = new Map<string, Map<string, PeerReviewsOfRoomUserData>>()

        // Initialize the structure for all user peer reviews
        for (const userPeerReview of validUserPeerReviews) {
          const roomId = userPeerReview.data.roomId
          const userId = userPeerReview.data.userId

          if (!byRoom.has(roomId)) {
            byRoom.set(roomId, new Map())
          }

          const roomMap = byRoom.get(roomId)!
          if (!roomMap.has(userId)) {
            const roomState = roomStates.find((rs) => rs.id === roomId)
            if (!roomState) continue

            const initialData: PeerReviewsOfRoomUserData = {
              peerReviews: new Map<string, ReviewScoresByQuestionId>(),
              peerReviewsExcludingNA: new Map<
                string,
                ReviewScoresByQuestionIdExcludingNA
              >(),
              notes: new Map<string, string>(),
              peerReviewersCount: 0,
              peerReviewsExcludingNACounts: new Map<string, number>(),
              peerReviewsExcludingNASums: new Map<string, number>(),
              peerReviewsExcludingNAAverages: new Map<string, number | null>(),
              peerReviewerAveragesExcludingNA: new Map<string, number | null>(),
              overallPeerReviewAverageExcludingNA: null,
              selfReview: null,
              selfReviewExcludingNAAverage: null,
              roomState,
            }
            roomMap.set(userId, initialData)
          }
        }

        // Process peer reviews
        for (const peerReview of peerReviews) {
          const roomId = peerReview.data.roomId
          const userId = peerReview.data.userId
          const reviewerUserId = peerReview.data.reviewerUserId

          const roomMap = byRoom.get(roomId)
          if (!roomMap) continue

          const userResult = roomMap.get(userId)
          if (!userResult) continue

          const review = peerReview.data.review

          const byReviewer =
            userResult.peerReviews.get(reviewerUserId) ||
            (() => {
              const newMap = new Map<string, 0 | 1 | 2 | 3>()
              userResult.peerReviews.set(reviewerUserId, newMap)
              return newMap
            })()

          const byReviewerExcludingNA =
            userResult.peerReviewsExcludingNA.get(reviewerUserId) ||
            (() => {
              const newMap = new Map<string, 1 | 2 | 3>()
              userResult.peerReviewsExcludingNA.set(reviewerUserId, newMap)
              return newMap
            })()

          for (const [questionId, score] of Object.entries(review)) {
            const question = questions.find((q) => q.id === questionId)
            if (!question) continue // We ignore such reviews

            if (score !== 0 && score !== 1 && score !== 2 && score !== 3)
              throw new Error('Invalid score')

            byReviewer.set(questionId, score)

            if (score !== 0) {
              byReviewerExcludingNA.set(questionId, score)

              const currentCount =
                userResult.peerReviewsExcludingNACounts.get(questionId) || 0
              const currentSum =
                userResult.peerReviewsExcludingNASums.get(questionId) || 0

              const newCount = currentCount + 1
              const newSum = currentSum + score
              const newAverage = Math.round((newSum / newCount) * 10) / 10

              userResult.peerReviewsExcludingNACounts.set(questionId, newCount)
              userResult.peerReviewsExcludingNASums.set(questionId, newSum)
              userResult.peerReviewsExcludingNAAverages.set(
                questionId,
                newAverage
              )
            }
          }

          const note = notes.find(
            (note) =>
              note.data.userId === userId &&
              note.data.reviewerUserId === reviewerUserId &&
              note.data.roomId === roomId
          )
          if (note) {
            userResult.notes.set(reviewerUserId, note.data.reviewNote)
          }
        }

        // Process self reviews
        for (const selfReview of selfReviews) {
          const roomId = selfReview.data.roomId
          const userId = selfReview.data.userId

          const roomMap = byRoom.get(roomId)
          if (!roomMap) continue

          const userResult = roomMap.get(userId)
          if (!userResult) continue

          const review = selfReview.data.review
          const selfReviewMap = new Map<string, 0 | 1 | 2 | 3>()

          for (const [questionId, score] of Object.entries(review)) {
            const question = questions.find((q) => q.id === questionId)
            if (!question) continue // We ignore such reviews

            if (score !== 0 && score !== 1 && score !== 2 && score !== 3)
              throw new Error('Invalid score')

            selfReviewMap.set(questionId, score)
          }

          const note = notes.find(
            (note) =>
              note.data.userId === userId &&
              note.data.reviewerUserId === userId &&
              note.data.roomId === roomId
          )
          if (note) {
            userResult.notes.set(userId, note.data.reviewNote)
          }

          userResult.selfReview = selfReviewMap
        }

        // Calculate aggregates for each user
        for (const roomMap of byRoom.values()) {
          for (const userResult of roomMap.values()) {
            userResult.peerReviewersCount = userResult.peerReviews.size

            if (userResult.selfReview !== null) {
              let selfReviewSum = 0
              let selfReviewCount = 0
              for (const score of userResult.selfReview.values()) {
                if (score !== 0) {
                  selfReviewSum += score
                  selfReviewCount += 1
                }
              }
              userResult.selfReviewExcludingNAAverage =
                selfReviewCount > 0
                  ? Math.round((selfReviewSum / selfReviewCount) * 10) / 10
                  : null
            }

            for (const [
              reviewerUserId,
              reviews,
            ] of userResult.peerReviewsExcludingNA.entries()) {
              if (reviews.size === 0) {
                userResult.peerReviewerAveragesExcludingNA.set(
                  reviewerUserId,
                  null
                )
                continue
              }
              let sum = 0
              let count = 0
              for (const score of reviews.values()) {
                sum += score
                count += 1
              }
              const average =
                count > 0 ? Math.round((sum / count) * 10) / 10 : null
              userResult.peerReviewerAveragesExcludingNA.set(
                reviewerUserId,
                average
              )
            }

            let overallCount = 0
            let overallSum = 0
            for (const questionId of userResult.peerReviewsExcludingNACounts.keys()) {
              overallCount +=
                userResult.peerReviewsExcludingNACounts.get(questionId) || 0
              overallSum +=
                userResult.peerReviewsExcludingNASums.get(questionId) || 0
            }
            userResult.overallPeerReviewAverageExcludingNA =
              overallCount > 0
                ? Math.round((overallSum / overallCount) * 10) / 10
                : null
          }
        }

        // Sort each room's users
        for (const [roomId, roomMap] of byRoom.entries()) {
          const sortedEntries = Array.from(roomMap.entries()).sort(
            ([userIdA, peerReviewsA], [userIdB, peerReviewsB]) => {
              const directionMod = this.revieweeSortOrder === 'asc' ? 1 : -1
              const userA = this.repository.userStore.getUser(userIdA)
              const userB = this.repository.userStore.getUser(userIdB)

              let comparison = 0

              if (this.revieweeSortStrategy === 'name') {
                const nameA = userA?.fullName || ''
                const nameB = userB?.fullName || ''
                comparison = nameA.localeCompare(nameB)
              } else if (this.revieweeSortStrategy === 'average') {
                const avgA =
                  peerReviewsA.overallPeerReviewAverageExcludingNA ?? 0
                const avgB =
                  peerReviewsB.overallPeerReviewAverageExcludingNA ?? 0
                comparison = avgA - avgB
              } else {
                const scoreA =
                  peerReviewsA.peerReviewsExcludingNAAverages.get(
                    this.revieweeSortStrategy
                  ) ?? 0
                const scoreB =
                  peerReviewsB.peerReviewsExcludingNAAverages.get(
                    this.revieweeSortStrategy
                  ) ?? 0
                comparison = scoreA - scoreB
              }

              return comparison * directionMod
            }
          )

          byRoom.set(roomId, new Map(sortedEntries))
        }

        return byRoom
      }
    )
  }

  @computed
  get peerReviewsByRoomReviewer(): LoadingState<
    Map<string, Map<string, PeerReviewsByRoomUserData>>
  > {
    return guardOnLoaded(
      [
        this.validUserPeerReviews,
        this.roomStateUPRReviewsByPeer,
        this.roomStateUPRReviewsBySelf,
        this.peerReviewQuestions,
        this.roomStates,
        this.roomStateUPRNotes,
      ],
      ([
        validUserPeerReviews,
        peerReviews,
        selfReviews,
        questions,
        roomStates,
        notes,
      ]) => {
        // First, group by roomId, then by reviewerUserId
        const byRoom = new Map<string, Map<string, PeerReviewsByRoomUserData>>()

        // Initialize the structure for all user peer reviews (keyed by reviewerUserId)
        for (const userPeerReview of validUserPeerReviews) {
          const roomId = userPeerReview.data.roomId
          const reviewerUserId = userPeerReview.data.userId

          if (!byRoom.has(roomId)) {
            byRoom.set(roomId, new Map())
          }

          const roomMap = byRoom.get(roomId)!
          if (!roomMap.has(reviewerUserId)) {
            const roomState = roomStates.find((rs) => rs.id === roomId)
            if (!roomState) continue

            roomMap.set(reviewerUserId, {
              peerReviews: new Map<string, ReviewScoresByQuestionId>(),
              peerReviewsExcludingNA: new Map<
                string,
                ReviewScoresByQuestionIdExcludingNA
              >(),
              notes: new Map<string, string>(),
              revieweesCount: 0,
              selfReview: null,
              incomplete: true,
              roomState,
            })
          }
        }

        // Process peer reviews
        for (const peerReview of peerReviews) {
          const roomId = peerReview.data.roomId
          const userId = peerReview.data.userId
          const reviewerUserId = peerReview.data.reviewerUserId

          const roomMap = byRoom.get(roomId)
          if (!roomMap) continue

          const reviewerResult = roomMap.get(reviewerUserId)
          if (!reviewerResult) continue

          const review = peerReview.data.review

          if (!reviewerResult.peerReviews.has(userId)) {
            reviewerResult.peerReviews.set(userId, new Map())
          }
          if (!reviewerResult.peerReviewsExcludingNA.has(userId)) {
            reviewerResult.peerReviewsExcludingNA.set(userId, new Map())
          }

          const revieweeReviews = reviewerResult.peerReviews.get(userId)!
          const revieweeReviewsExcludingNA =
            reviewerResult.peerReviewsExcludingNA.get(userId)!

          for (const [questionId, score] of Object.entries(review)) {
            const question = questions.find((q) => q.id === questionId)
            if (!question) continue

            if (score !== 0 && score !== 1 && score !== 2 && score !== 3)
              continue

            revieweeReviews.set(questionId, score)

            if (score !== 0) {
              revieweeReviewsExcludingNA.set(questionId, score)
            }
          }

          const note = notes.find(
            (note) =>
              note.data.userId === userId &&
              note.data.reviewerUserId === reviewerUserId &&
              note.data.roomId === roomId
          )
          if (note) {
            reviewerResult.notes.set(userId, note.data.reviewNote)
          }
        }

        // Process self reviews
        if (!this.peerReviewExcludesSelfReview) {
          for (const selfReview of selfReviews) {
            const roomId = selfReview.data.roomId
            const reviewerUserId = selfReview.data.reviewerUserId

            const roomMap = byRoom.get(roomId)
            if (!roomMap) continue

            const reviewerResult = roomMap.get(reviewerUserId)
            if (!reviewerResult) continue

            const review = selfReview.data.review
            const selfReviewMap = new Map<string, 0 | 1 | 2 | 3>()

            for (const [questionId, score] of Object.entries(review)) {
              const question = questions.find((q) => q.id === questionId)
              if (!question) continue

              if (score !== 0 && score !== 1 && score !== 2 && score !== 3)
                continue

              selfReviewMap.set(questionId, score)
            }

            const note = notes.find(
              (note) =>
                note.data.userId === reviewerUserId &&
                note.data.reviewerUserId === reviewerUserId &&
                note.data.roomId === roomId
            )
            if (note) {
              reviewerResult.notes.set(reviewerUserId, note.data.reviewNote)
            }

            reviewerResult.selfReview = selfReviewMap
          }
        }

        // Calculate aggregates for each reviewer
        for (const roomMap of byRoom.values()) {
          for (const reviewerResult of roomMap.values()) {
            reviewerResult.revieweesCount = reviewerResult.peerReviews.size
            if (
              !this.peerReviewExcludesSelfReview &&
              reviewerResult.selfReview !== null
            ) {
              reviewerResult.revieweesCount += 1
            }

            // incomplete is true when the student hasn't reviewed anyone at all
            reviewerResult.incomplete = reviewerResult.revieweesCount === 0
          }
        }

        return byRoom
      }
    )
  }

  @computed
  get peerAndSelfReviewsByQuestion(): LoadingState<Map<string, number | null>> {
    return guardOnLoaded(
      [
        this.peerReviewQuestions,
        this.roomStateUPRReviewsByPeer,
        this.roomStateUPRReviewsBySelf,
      ],
      ([
        peerReviewQuestions,
        roomStateUPRReviewsByPeer,
        roomStateUPRReviewsBySelf,
      ]) => {
        const byQuestion = new Map<string, { count: number; sum: number }>()

        for (const question of peerReviewQuestions) {
          byQuestion.set(question.id, { count: 0, sum: 0 })
        }

        for (const peerReview of roomStateUPRReviewsByPeer) {
          const review = peerReview.data.review

          for (const [questionId, score] of Object.entries(review)) {
            if (score === 0) continue

            const question = peerReviewQuestions.find(
              (q) => q.id === questionId
            )
            if (!question) continue

            const result = byQuestion.get(questionId)
            if (result) {
              result.count += 1
              result.sum += score
            }
          }
        }

        for (const selfReview of roomStateUPRReviewsBySelf) {
          const review = selfReview.data.review

          for (const [questionId, score] of Object.entries(review)) {
            if (score === 0) continue

            const question = peerReviewQuestions.find(
              (q) => q.id === questionId
            )
            if (!question) continue

            const result = byQuestion.get(questionId)
            if (result) {
              result.count += 1
              result.sum += score
            }
          }
        }

        const averages = new Map<string, number | null>()

        for (const [questionId, result] of byQuestion.entries()) {
          averages.set(
            questionId,
            result && result.count > 0
              ? Math.round((result.sum / result.count) * 10) / 10
              : null
          )
        }

        return averages
      }
    )
  }
}

/** Keyed by questionId: */
type ReviewScoresByQuestionId = Map<string, 0 | 1 | 2 | 3>

/** Keyed by questionId: */
type ReviewScoresByQuestionIdExcludingNA = Map<string, 1 | 2 | 3>

export type PeerReviewsOfRoomUserData = {
  /** Keyed by reviewerUserId: */
  peerReviews: Map<string, ReviewScoresByQuestionId>
  /** Keyed by reviewerUserId: */
  peerReviewsExcludingNA: Map<string, ReviewScoresByQuestionIdExcludingNA>
  /** Keyed by questionId: */
  peerReviewsExcludingNACounts: Map<string, number>
  /** Keyed by questionId: */
  peerReviewsExcludingNASums: Map<string, number>
  /** Keyed by questionId: */
  peerReviewsExcludingNAAverages: Map<string, number | null>
  /** Keyed by reviewerUserId: */
  peerReviewerAveragesExcludingNA: Map<string, number | null>
  /** Keyed by reviewerUserId: */
  notes: Map<string, string>
  overallPeerReviewAverageExcludingNA: number | null
  peerReviewersCount: number
  selfReview: ReviewScoresByQuestionId | null
  selfReviewExcludingNAAverage: number | null
  roomState: RoomState
}

export type PeerReviewsByRoomUserData = {
  /** Keyed by revieweeUserId: */
  peerReviews: Map<string, ReviewScoresByQuestionId>
  /** Keyed by revieweeUserId: */
  peerReviewsExcludingNA: Map<string, ReviewScoresByQuestionIdExcludingNA>
  /** Keyed by revieweeUserId: */
  notes: Map<string, string>
  /** Includes self-review if present */
  revieweesCount: number
  /** Self-review scores, keyed by questionId. Null if no self-review exists. */
  selfReview: ReviewScoresByQuestionId | null
  /** Whether the student hasn't reviewed anyone at all (peerReviews.size === 0) */
  incomplete: boolean
  roomState: RoomState
}
